import React, { useState, useEffect } from 'react';
import api from '../api';
import { BookOpen, Video, FileText } from 'lucide-react';

export default function Education() {
  const [resources, setResources] = useState({ cv_resources: [], interview_resources: [] });
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('cv');

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await api.get('/api/education');
        setResources(response.data);
      } catch (err) {
        console.error('Failed to fetch resources:', err);
        // Fallback sample resources
        setResources({
          cv_resources: [
            { id: 'cv-1', title: 'Mastering CVs: Land Your Dream Job', content: 'Concise, achievement-focused CV templates and examples to help you stand out.', image_url: '', type: 'cv' },
            { id: 'cv-2', title: 'CV Checklist', content: 'Step-by-step checklist to quickly improve your CV.', image_url: '', type: 'cv' }
          ],
          interview_resources: [
            { id: 'int-1', title: 'Ace Your Remote Interview', content: 'How to prepare and present your best self in a virtual interview.', image_url: '', type: 'interview' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p>Loading resources...</p></div>;
  }

  const currentResources = selectedTab === 'cv' ? resources.cv_resources : resources.interview_resources;

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Career Resources</h1>
          <p className="text-xl text-gray-600">Learn how to write winning CVs and ace your interviews</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setSelectedTab('cv')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              selectedTab === 'cv'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FileText size={20} />
              <span>CV Writing Guide</span>
            </div>
          </button>
          <button
            onClick={() => setSelectedTab('interview')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              selectedTab === 'interview'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Video size={20} />
              <span>Interview Tips</span>
            </div>
          </button>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentResources.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-lg p-12 text-center">
              <BookOpen className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 text-lg">No resources available yet.</p>
            </div>
          ) : (
            currentResources.map((resource) => (
              <div key={resource.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition flex flex-col">
                {resource.image_url ? (
                  <img
                    src={resource.image_url}
                    alt={resource.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">{resource.title.split(' ').slice(0,3).join(' ')}</div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold mb-3">{resource.title}</h3>
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800">{resource.type === 'cv' ? 'CV Guide' : 'Interview'}</span>
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-3">{resource.content}</p>

                  <div className="mt-auto">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700">Read Full Guide →</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4">CV Writing Tips</h2>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-sm">✓</span>
                <span>Keep it to 1-2 pages maximum</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-sm">✓</span>
                <span>Use action verbs to describe achievements</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-sm">✓</span>
                <span>Include quantifiable results</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-sm">✓</span>
                <span>Tailor it to each job application</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Interview Preparation</h2>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-400 flex items-center justify-center text-sm">✓</span>
                <span>Research the company thoroughly</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-400 flex items-center justify-center text-sm">✓</span>
                <span>Practice common interview questions</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-400 flex items-center justify-center text-sm">✓</span>
                <span>Dress appropriately for the role</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-400 flex items-center justify-center text-sm">✓</span>
                <span>Ask thoughtful questions</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
